import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { parseFriendDeepLink, requestFriendship } from "@/lib/friends";
import { LoadingBlock } from "@/components/AsyncState";

export const Route = createFileRoute("/friends/add")({
  validateSearch: (search: Record<string, unknown>) => ({
    uid: typeof search.uid === "string" ? search.uid : undefined,
    u: typeof search.u === "string" ? search.u : undefined,
  }),
  component: FriendsAddPage,
});

function FriendsAddPage() {
  const { t } = useTranslation("consumer");
  const { user, hydrated, isGuest } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [status, setStatus] = useState<"working" | "done" | "error">("working");
  const [message, setMessage] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (!hydrated || ran.current) return;
    const uid =
      search.uid ??
      search.u ??
      parseFriendDeepLink(
        typeof window !== "undefined" ? window.location.href : "",
      );
    if (!uid) {
      setStatus("error");
      setMessage(t("friends.invalidLink"));
      return;
    }
    if (isGuest || !user?.id) {
      setStatus("error");
      setMessage(t("friends.signInToAdd"));
      return;
    }
    if (uid === user.id) {
      setStatus("error");
      setMessage(t("friends.cannotAddSelf"));
      return;
    }

    ran.current = true;
    void requestFriendship(uid)
      .then(() => {
        setStatus("done");
        toast.success(t("friends.requestSent"));
        void navigate({ to: "/profile", search: { tab: "friends" } });
      })
      .catch((err: unknown) => {
        const code = err instanceof Error ? err.message : "error";
        setStatus("error");
        if (code === "already_friends") setMessage(t("friends.alreadyFriends"));
        else if (code === "already_pending") setMessage(t("friends.alreadyPending"));
        else if (code === "blocked") setMessage(t("friends.blockedCannotRequest"));
        else setMessage(t("friends.requestFailed"));
      });
  }, [hydrated, isGuest, user?.id, search.uid, search.u, navigate, t]);

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center px-6">
      {status === "working" && <LoadingBlock label={t("friends.sendingRequest")} />}
      {status !== "working" && (
        <p className="text-center text-sm text-muted-foreground">{message}</p>
      )}
    </div>
  );
}
